export const DEFAULT_DOWNLOAD_REPORT_TYPE = 'order';
export const DEFAULT_DOWNLOAD_REPORT_SUBTYPE = 'origin';
export const DEFAULT_DATE_BASIS = 'creation';
export const DEFAULT_PAYMENT_TYPE = 'both';
export const DEFAULT_REPORT_MODE = 'origin';
export const DEFAULT_UPSALE_MODE = 'quantity';
export const DEFAULT_CUSTOMER_RANK = 'frequency';
export const DEFAULT_REPORT_LAYOUT = 'summary';
export const DOWNLOAD_REPORT_PER_PAGE = 50;

export const REPORT_MODES = ['origin', 'attribution'];
export const UPSALE_MODES = ['quantity', 'value'];
export const CUSTOMER_RANKS = ['frequency', 'value'];
export const REPORT_LAYOUTS = ['summary', 'details'];
export const PRODUCT_GRAINS = ['base', 'variant'];
export const DEFAULT_PRODUCT_GRAIN = 'base';

export const USER_LAYOUT_SUBTYPES = [
  'cross_sale_upsale_approve',
  'order_creation',
];

export const DATE_BASIS_VALUES = [
  'creation',
  'approved',
  'in_transit',
  'delivered',
];

export const PAYMENT_TYPES = ['both', 'paid', 'due'];

export const dateBasisValuesFor = (reportType, reportSubtype) => {
  if (reportType === 'order' && reportSubtype === 'origin') return ['creation'];
  if (reportType === 'user' && reportSubtype === 'order_creation') {
    return ['creation'];
  }
  if (reportType === 'user' && reportSubtype === 'cross_sale_upsale_approve') {
    return ['approved'];
  }
  if (reportType === 'user' && reportSubtype === 'order_status_change') {
    return [];
  }
  if (reportType === 'customer' && reportSubtype === 'top_customer_repeat') {
    return [];
  }
  if (reportType === 'inventory') return [];
  if (
    ['user', 'order', 'production', 'customer', 'delivery_partner'].includes(
      reportType
    )
  ) {
    return DATE_BASIS_VALUES;
  }
  return [];
};

export const lockedDateBasis = (reportType, reportSubtype) => {
  const values = dateBasisValuesFor(reportType, reportSubtype);
  return values.length === 1 ? values[0] : '';
};

export const showsDateBasis = (reportType, reportSubtype) =>
  dateBasisValuesFor(reportType, reportSubtype).length > 1;

export const showsReportMode = (reportType, reportSubtype) =>
  reportType === 'order' && reportSubtype === 'origin';

export const resolvedReportMode = value =>
  REPORT_MODES.includes(value) ? value : DEFAULT_REPORT_MODE;

export const showsUpsaleMode = (reportType, reportSubtype) =>
  reportType === 'user' && reportSubtype === 'order_upsale';

export const showsReportLayout = (reportType, reportSubtype) =>
  reportType === 'user' && USER_LAYOUT_SUBTYPES.includes(reportSubtype);

export const showsPermissionGroups = showsReportLayout;

export const resolvedReportLayout = value =>
  REPORT_LAYOUTS.includes(value) ? value : DEFAULT_REPORT_LAYOUT;

export const showsCustomerRank = (reportType, reportSubtype) =>
  reportType === 'customer' && reportSubtype === 'top_customer_repeat';

export const resolvedUpsaleMode = value =>
  UPSALE_MODES.includes(value) ? value : DEFAULT_UPSALE_MODE;

export const resolvedCustomerRank = value =>
  CUSTOMER_RANKS.includes(value) ? value : DEFAULT_CUSTOMER_RANK;

export const showsSingleStockDate = (reportType, reportSubtype) =>
  reportType === 'inventory' && reportSubtype === 'stock_summary';

export const PAID_DUE_COLUMNS = ['orders', 'total', 'advance', 'paid', 'due'];

export const PRODUCTION_BASE_COLUMNS = ['orders', 'qty', 'amount'];

export const PRODUCTION_VARIANT_EXTRA = ['sku', 'product_name'];

export const CUSTOMER_TOP_LEAD = ['customer_name', 'phone'];

export const CUSTOMER_TOP_METRICS = ['orders', 'amount'];

export const CUSTOMER_PRODUCT_LEAD = ['customer_name', 'phone', 'product_name'];

export const CUSTOMER_PRODUCT_METRICS = ['orders', 'qty', 'amount'];

export const INVENTORY_STOCK_LEAD = ['product_name', 'sku'];

export const INVENTORY_STOCK_METRICS = [
  'on_hand',
  'processing',
  'in_transit',
  'available',
  'shortage',
];

export const INVENTORY_SHORTAGE_LEAD = [
  'sale_date',
  'product_name',
  'sku',
  'category',
];

export const INVENTORY_SHORTAGE_METRICS = [
  'morning_stock',
  'day_end_stock',
  'shortage',
];

export const INVENTORY_DAMAGE_LEAD = ['product_name', 'sku'];

export const INVENTORY_DAMAGE_METRICS = ['adjustments', 'qty'];

export const INVENTORY_EXPIRY_LEAD = ['product_name', 'sku', 'expires_on'];

export const INVENTORY_EXPIRY_METRICS = ['qty'];

export const DELIVERY_SHIPMENT_LEAD = ['partner_name', 'provider'];

export const DELIVERY_SHIPMENT_METRICS = [
  'shipments',
  'orders',
  'delivered',
  'cancelled',
];

const INVENTORY_LEAD = {
  stock_summary: INVENTORY_STOCK_LEAD,
  daily_stock_shortage: INVENTORY_SHORTAGE_LEAD,
  damage: INVENTORY_DAMAGE_LEAD,
  expiries: INVENTORY_EXPIRY_LEAD,
};

const INVENTORY_METRICS = {
  stock_summary: INVENTORY_STOCK_METRICS,
  daily_stock_shortage: INVENTORY_SHORTAGE_METRICS,
  damage: INVENTORY_DAMAGE_METRICS,
  expiries: INVENTORY_EXPIRY_METRICS,
};

export const inventoryLeadColumns = subtype =>
  INVENTORY_LEAD[subtype] || INVENTORY_STOCK_LEAD;

export const inventoryMetricColumns = subtype =>
  INVENTORY_METRICS[subtype] || INVENTORY_STOCK_METRICS;

export const customerLeadColumns = subtype =>
  subtype === 'product_sales_to_customer'
    ? CUSTOMER_PRODUCT_LEAD
    : CUSTOMER_TOP_LEAD;

export const customerMetricColumns = subtype =>
  subtype === 'product_sales_to_customer'
    ? CUSTOMER_PRODUCT_METRICS
    : CUSTOMER_TOP_METRICS;

export const ORIGIN_COUNT_COLUMNS = [
  'pending',
  'on_hold',
  'approved',
  'processing',
  'ready_to_go',
  'in_transit',
  'delivered',
  'returned',
  'cancelled',
  'flagged',
];

export const ORIGIN_RATE_COLUMNS = [
  'delivery_rate',
  'return_rate',
  'cancelled_rate',
];

export const USER_APPROVE_COLUMNS = [
  'assigned',
  'approved',
  'upsale',
  'approved_plain',
  'cancelled',
  'on_hold',
];

export const USER_CREATION_COLUMNS = ['assigned', 'assigned_amount'];

export const USER_UPSALE_COLUMNS = [
  'sale_date',
  'invoice_number',
  'order_source',
  'sku',
  'product_name',
  'qty',
  'discount',
  'amount',
  'status',
];

export const USER_STATUS_CHANGE_COLUMNS = [
  'status_changes',
  ...ORIGIN_COUNT_COLUMNS,
];

export const userDetailColumns = subtype => {
  if (subtype === 'cross_sale_upsale_approve') {
    return ['invoice_number', 'agent_name', 'amount', 'upsale'];
  }
  if (subtype === 'order_creation') {
    return ['invoice_number', 'agent_name', 'amount'];
  }
  return [];
};

export const userPreviewColumns = subtype => {
  switch (subtype) {
    case 'cross_sale_upsale_approve':
      return USER_APPROVE_COLUMNS;
    case 'order_creation':
      return USER_CREATION_COLUMNS;
    case 'order_upsale':
      return USER_UPSALE_COLUMNS;
    case 'order_status_change':
      return USER_STATUS_CHANGE_COLUMNS;
    case 'based_on_product':
      return ['sku', 'product_name', 'orders', 'qty', 'amount'];
    default:
      return [];
  }
};

export const DOWNLOAD_REPORT_TYPES = [
  {
    value: 'user',
    subtypes: [
      'cross_sale_upsale_approve',
      'order_status_change',
      'order_upsale',
      'order_creation',
      'based_on_product',
    ],
  },
  {
    value: 'order',
    subtypes: ['origin', 'paid_and_due_amount'],
  },
  {
    value: 'production',
    subtypes: [
      'sales_by_base_product',
      'sales_by_variant',
      'daily_product_wise_sales',
    ],
  },
  {
    value: 'inventory',
    subtypes: ['stock_summary', 'daily_stock_shortage', 'damage', 'expiries'],
  },
  {
    value: 'customer',
    subtypes: ['top_customer_repeat', 'product_sales_to_customer'],
  },
  {
    value: 'delivery_partner',
    subtypes: ['shipments'],
  },
];

const AVAILABLE_PREVIEWS = new Set([
  'order:origin',
  'order:paid_and_due_amount',
  'user:cross_sale_upsale_approve',
  'user:order_status_change',
  'user:order_upsale',
  'user:order_creation',
  'production:sales_by_base_product',
  'production:sales_by_variant',
  'customer:top_customer_repeat',
  'customer:product_sales_to_customer',
  'inventory:stock_summary',
  'inventory:daily_stock_shortage',
  'inventory:damage',
  'inventory:expiries',
  'production:daily_product_wise_sales',
  'user:based_on_product',
  'delivery_partner:shipments',
]);

export const typeLabelKey = value =>
  `DOWNLOAD_REPORTS.TYPES.${String(value || '').toUpperCase()}`;

export const subtypeLabelKey = value =>
  `DOWNLOAD_REPORTS.SUBTYPES.${String(value || '').toUpperCase()}`;

export const dateBasisLabelKey = value =>
  `DOWNLOAD_REPORTS.FORM.DATE_BASIS_OPTIONS.${String(value || '').toUpperCase()}`;

export const reportModeLabelKey = value =>
  `DOWNLOAD_REPORTS.FORM.REPORT_MODE_OPTIONS.${String(value || '').toUpperCase()}`;

export const upsaleModeLabelKey = value =>
  `DOWNLOAD_REPORTS.FORM.UPSALE_MODE_OPTIONS.${String(value || '').toUpperCase()}`;

export const reportLayoutLabelKey = value =>
  `DOWNLOAD_REPORTS.FORM.REPORT_LAYOUT_OPTIONS.${String(value || '').toUpperCase()}`;

export const customerRankLabelKey = value =>
  `DOWNLOAD_REPORTS.FORM.CUSTOMER_RANK_OPTIONS.${String(value || '').toUpperCase()}`;

export const subtypesForType = reportType =>
  DOWNLOAD_REPORT_TYPES.find(entry => entry.value === reportType)?.subtypes ||
  [];

export const defaultSubtypeForType = reportType =>
  subtypesForType(reportType)[0] || DEFAULT_DOWNLOAD_REPORT_SUBTYPE;

export const isDownloadPreviewAvailable = (reportType, reportSubtype) =>
  AVAILABLE_PREVIEWS.has(`${reportType}:${reportSubtype}`);

export const showsPaymentType = (reportType, reportSubtype) =>
  reportType === 'order' && reportSubtype === 'paid_and_due_amount';

export const resolvedPaymentType = value =>
  PAYMENT_TYPES.includes(value) ? value : DEFAULT_PAYMENT_TYPE;

export const paymentTypeLabelKey = value =>
  `DOWNLOAD_REPORTS.FORM.PAYMENT_TYPE_OPTIONS.${String(value || '').toUpperCase()}`;

export const showsOrderSources = (reportType, reportSubtype) => {
  if (reportType === 'order') return reportSubtype === 'paid_and_due_amount';
  if (reportType !== 'user') return false;

  return ['order_status_change', 'order_upsale', 'based_on_product'].includes(
    reportSubtype
  );
};

export const showsStatusFilter = () => false;

export const showsEmployeeSearch = reportType => reportType === 'user';

const INVENTORY_WAREHOUSE_SUBTYPES = new Set([
  'stock_summary',
  'daily_stock_shortage',
  'damage',
  'expiries',
]);

export const showsWarehouseFilter = (reportType, reportSubtype) =>
  reportType === 'inventory' && INVENTORY_WAREHOUSE_SUBTYPES.has(reportSubtype);

export const showsPartnerFilter = (reportType, reportSubtype) =>
  (reportType === 'delivery_partner' && reportSubtype === 'shipments') ||
  (reportType === 'order' && reportSubtype === 'paid_and_due_amount');

export const showsProductGrain = (reportType, reportSubtype) =>
  reportType === 'production' && reportSubtype === 'daily_product_wise_sales';

export const resolvedProductGrain = value =>
  PRODUCT_GRAINS.includes(value) ? value : DEFAULT_PRODUCT_GRAIN;

export const productGrainLabelKey = value =>
  `DOWNLOAD_REPORTS.FORM.PRODUCT_GRAIN_OPTIONS.${String(value || '').toUpperCase()}`;

export const showsProductFilter = (reportType, reportSubtype) =>
  (reportType === 'production' &&
    (reportSubtype === 'sales_by_base_product' ||
      reportSubtype === 'sales_by_variant' ||
      reportSubtype === 'daily_product_wise_sales')) ||
  (reportType === 'customer' && reportSubtype === 'product_sales_to_customer');

// sales_by_variant groups by line SKU. Daily product-wise follows the Base/Variant radio.
export const productFilterGrain = (
  reportType,
  reportSubtype,
  selectedGrain
) => {
  if (!showsProductFilter(reportType, reportSubtype)) return '';
  if (showsProductGrain(reportType, reportSubtype)) {
    return resolvedProductGrain(selectedGrain);
  }
  if (reportType === 'production' && reportSubtype === 'sales_by_variant') {
    return 'variant';
  }
  return 'base';
};

const padTwo = value => String(value).padStart(2, '0');

export const formatLocalDateTimeKey = value => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = padTwo(date.getMonth() + 1);
  const day = padTwo(date.getDate());
  const hours = padTwo(date.getHours());
  const minutes = padTwo(date.getMinutes());
  const seconds = padTwo(date.getSeconds());
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absolute = Math.abs(offsetMinutes);
  const offsetHours = padTwo(Math.floor(absolute / 60));
  const offsetMins = padTwo(absolute % 60);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
};
